import { ContactStatus } from '@wonderschool/common-base-types';
import invariant from 'tiny-invariant';

export interface DetermineContactStatusParams {
  sendInvitation: boolean;
  emailUsage: {
    hasAuthUser: boolean;
    uid: string | null;
    existingContacts: Array<{
      id: string;
      status: ContactStatus;
      invitationId: string | null;
      uid: string | null;
    }> | null;
  };
  currentContact: {
    id?: string;
    status: ContactStatus;
    invitationId: string | null;
  };
}

export type DetermineContactStatusResult =
  | {
      status: ContactStatus.verified;
      invitationId: string | null;
      uid: string;
    }
  | {
      status: ContactStatus.invited;
      invitationId: string;
    }
  | {
      status: ContactStatus.inviting;
      invitationId: string | null | undefined;
    }
  | {
      status: ContactStatus;
    };

/**
 * Processes contact invitation status assignment based on email usage and existing contacts.
 *
 * @param params - Configuration object containing all necessary parameters
 * @param params.sendInvitation - Whether to send an invitation to the contact
 * @param params.emailUsage - Email usage information containing auth user status and existing contacts in _other_ organizations
 * @param params.currentContact - The current contact information containing status and invitation ID for the contact being added or edited in the current organization
 *
 * @returns ProcessContactInvitationResult Result object containing:
 *   - status: The determined ContactStatus for the contact
 *   - invitationId: The invitation ID (null if not applicable)
 *   - uid: The user ID if the contact is verified
 *
 * @description
 * This function determines the appropriate contact status based on email usage information and current contact status:
 *
 * 1. **Has Auth User**: Sets status to 'verified', clears invitation ID, assigns UID
 * 2. **Has Existing Contacts** (priority order: verified > invited):
 *    - Only inherits status from existing contacts if they are 'verified' or 'invited'
 *    - Searches for verified contacts first, then invited
 *    - If existing contact is 'verified': always inherit verified status and ignore sendInvitation
 *    - If sendInvitation is true and existing contact is 'invited': sets status to 'inviting' and preserves existing invitation ID
 *    - If sendInvitation is false and existing contact is 'invited': inherits status and invitation ID
 *    - If no verified or invited contacts exist: treats as new contact
 * 3. **No Auth User and No Existing Contacts** (or no verified/invited contacts):
 *    - If currentContact.status is 'verified': keep verified status and ignore sendInvitation
 *    - Sets status to 'new' if sendInvitation is false
 *    - Sets status to 'inviting' if sendInvitation is true
 *    - Preserve currentContact.invitationId when available
 */
export function determineContactStatus({
  sendInvitation,
  emailUsage,
  currentContact,
}: DetermineContactStatusParams): DetermineContactStatusResult | undefined {
  // If currentContact.status is 'verified': make no changes and ignore sendInvitation
  // this should be impossible since the UI won't allow email to change or an
  // invitation to be sent if the existing contact is verified
  if (currentContact.status === ContactStatus.verified) {
    return undefined;
  }

  // 1. Has Auth User: Sets status to 'verified', clears invitation ID, assigns UID
  if (emailUsage.hasAuthUser && emailUsage.uid) {
    return {
      status: ContactStatus.verified,
      invitationId: null,
      uid: emailUsage.uid,
    };
  }

  // 2. Has Existing Contacts
  // Filter out the current contact from existing contacts to avoid comparing against itself
  const existingContacts = emailUsage.existingContacts?.filter(
    (contact) => !currentContact.id || contact.id !== currentContact.id
  );
  if (existingContacts && existingContacts.length > 0) {
    // Find existing contacts with priority order: verified > invited
    // Only inherit status from verified or invited contacts
    const verifiedContact = existingContacts.find((contact) => contact.status === ContactStatus.verified);
    const invitedContact = existingContacts.find((contact) => contact.status === ContactStatus.invited);

    const relevantContact = verifiedContact || invitedContact;

    if (relevantContact) {
      // If existing contact is 'verified': always inherit verified status and ignore sendInvitation
      if (relevantContact.status === ContactStatus.verified) {
        invariant(relevantContact.uid, 'Verified contact must have a uid');
        return {
          status: ContactStatus.verified,
          invitationId: relevantContact.invitationId ?? null,
          uid: relevantContact.uid,
        };
      }

      if (sendInvitation) {
        // Sets status to 'inviting' and preserves existing invitation ID
        return {
          status: ContactStatus.inviting,
          invitationId: relevantContact.invitationId ?? null,
        };
      }

      // Inherits status and invitation ID
      return {
        status: relevantContact.status,
        invitationId: relevantContact.invitationId ?? null,
      };
    }

    // If no verified or invited contacts exist: treat as new contact (fall through to case 3)
  }

  // 3. No Auth User and No Existing Contacts (or no verified/invited contacts)

  // Sets status to 'inviting' if sendInvitation is true
  // Preserve currentContact.invitationId when available
  if (sendInvitation) {
    return {
      status: ContactStatus.inviting,
      invitationId: currentContact.invitationId ?? null,
    };
  }

  return {
    status: ContactStatus.new,
  };
}
